import MapView from '@arcgis/core/views/MapView';
import * as locator from '@arcgis/core/rest/locator.js';
import Point from '@arcgis/core/geometry/Point';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Geometry from '@arcgis/core/geometry/Geometry';
import Extent from '@arcgis/core/geometry/Extent';
import type {
  SearchResult,
  SuggestResult,
  BaseSearchResults,
  BaseSearchResponse,
  SearchResults
} from '@arcgis/core/widgets/Search/types.js';
// import Polygon = __esri.geometry.Polygon;
import * as unionOperator from "@arcgis/core/geometry/operators/unionOperator";
import * as intersectsOperator from "@arcgis/core/geometry/operators/intersectsOperator";
import * as touchesOperator from "@arcgis/core/geometry/operators/touchesOperator";
import * as distanceOperator from "@arcgis/core/geometry/operators/distanceOperator";
import * as geodesicBufferOperator from "@arcgis/core/geometry/operators/geodesicBufferOperator";

import Graphic from "@arcgis/core/Graphic";
import Polygon from '@arcgis/core/geometry/Polygon';

/**
 * Disables all mouse-based zooming on a MapView — wheel scroll, double-click,
 * drag, Shift+drag, keyboard +/-, and arrow keys — returning the same view.
 *
 * @param view - The ArcGIS MapView to disable zooming on.
 * @returns The same MapView with all zoom gestures disabled.
 */
export function disableZooming(view: MapView) {
  if (view.popup) {
    view.popup.dockEnabled = true;
    view.popup.actions = [] as any;
  }
  // disable mouse wheel scroll zooming on the view
  view.on('mouse-wheel', (e) => e.stopPropagation());
  // disable zooming via double-click on the view
  view.on('double-click', (e) => e.stopPropagation());
  // disable zooming out via double-click + Control on the view
  view.on('double-click', ['Control'], (e) => e.stopPropagation());
  // disables pinch-zoom and panning on the view
  view.on('drag', (e) => e.stopPropagation());
  // disable the view's zoom box to prevent the Shift + drag
  // and Shift + Control + drag zoom gestures.
  view.on('drag', ['Shift'], (e) => e.stopPropagation());
  view.on('drag', ['Shift', 'Control'], (e) => e.stopPropagation());
  // prevents zooming with the + and - keys
  view.on('key-down', (event) => {
    const prohibitedKeys = ['+', '-', 'Shift', '_', '=', 'ArrowUp', 'ArrowDown', 'ArrowRight', 'ArrowLeft'];
    const keyPressed = event.key;
    if (prohibitedKeys.indexOf(keyPressed) !== -1) {
      event.stopPropagation();
    }
  });

  return view;
}

const locatorUrl = 'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer';

/**
 * Looks up a city's geocoded locations for a given name near a reference point.
 *
 * @param city - The city name to look up.
 * @param point - A reference `Point` near which to search for the city.
 * @returns A promise resolving to the geocoder's response with matching city locations.
 */
export async function findCity(city: string, point: Point) {
  const response = await locator.addressToLocations(locatorUrl, {
    address: { City: city },
    location: point,
    categories: ['City']
  });
  return response;
}

/**
 * Suggests city names from the ArcGIS geocoder near a reference point.
 *
 * @param city - The partial or full city name to suggest.
 * @param point - A reference `Point` near which to search for city suggestions.
 * @returns A promise resolving to the geocoder's city suggestion results.
 */
export async function suggestCity(city: string, point: Point) {
  const response = await locator.suggestLocations(locatorUrl, {
    text: city,
    location: point,
    categories: ['City']
  });
  return response;
}

/**
 * Queries a FeatureLayer for features that intersect 5-mile geodesic buffers around given suggestions.
 *
 * @param layerToCheck - The FeatureLayer to query for existing features.
 * @param suggestions - Arrays of SuggestionResult objects to check against the layer.
 * @returns A promise resolving to an array of query result sets matching the suggestions.
 */
export const getExisting = async (layerToCheck: FeatureLayer, suggestions:SuggestResult[]) => {
  const existingProp = [];
    for (const result of suggestions) {
      const { extent } = result as any;
      if (!geodesicBufferOperator.isLoaded()) await geodesicBufferOperator.load();
      //@ts-ignore
      const buffer = geodesicBufferOperator.execute(extent, 5, { unit: "miles" });
      const results = await layerToCheck.queryFeatures({ geometry: buffer as Geometry, returnGeometry: false});
      //@ts-ignore
      existingProp.push(results);
    }
  return existingProp;
}

/**
 * Renders a polygon geometry as a filled, stroked image on a canvas and returns a PNG data URL.
 *
 * Scales the polygon's extent to fit a fixed width with an optional padding, fills it in
 * gray, strokes it in black, and returns the result as `data:image/png;base64,...`.
 *
 * @param geometry - The polygon geometry to render.
 * @param width - The canvas width in pixels (defaults to 500).
 * @param padding - Padding in pixels to add around the scaled geometry (defaults to 0).
 * @returns A PNG data URL representing the rendered polygon.
 */
export function renderFeatureOnCanvas(geometry: Polygon,width=500, padding=0): string {
  const extent: Extent = geometry.extent!;
  const { xmin: minX, xmax: maxX, ymin: minY, ymax: maxY } = extent;
  const extentWidth = maxX - minX;
  const extentHeight = maxY - minY;

  const aspectRatio = extentWidth / extentHeight;
  const canvasWidth = width + 2 * padding;
  const canvasHeight = (width / aspectRatio) + 2 * padding;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas context could not be retrieved.');

  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const scaleX = width / extentWidth;
  const scaleY = (canvasHeight - 2 * padding) / extentHeight;

  context.clearRect(0, 0, canvas.width, canvas.height);
  context.beginPath();

  geometry.rings.forEach((ring: number[][]) => {
    ring.forEach(([x = 0, y = 0], index: number) => {
      const scaledX = padding + (x - minX) * scaleX;
      const scaledY = canvas.height - padding - (y - minY) * scaleY;
      if (index === 0) context.moveTo(scaledX, scaledY);
      else context.lineTo(scaledX, scaledY);
    });
  });

  context.closePath();
  context.fillStyle = 'rgb(73,72,72)';
  context.fill();
  context.strokeStyle = '#000000';
  context.lineWidth = 2;
  context.stroke();

  return canvas.toDataURL('image/png');
}


/**
 * Computes a unified geometry and bounding extent from an array of graphics
 * containing polygon geometries.
 *
 * Attempts a topological union via `unionOperator.executeMany`; falls back to
 * combining individual polygon extents if the union yields no usable extent.
 * Returns `null` when no valid polygons are found or spatial references are mixed.
 *
 * @param graphics - Array of ArcGIS `Graphic` objects whose geometries should be combined.
 * @returns The union geometry and its bounding extent, or `null` if the input is empty/invalid.
 */
export function combineExtent(
  graphics: Graphic[]
): { unionGeometry: Geometry; extent: Extent } | null {
  if (!graphics?.length) return null;

  // Collect valid polygon geometries
  const polygons: Polygon[] = graphics
    .map((g) => g.geometry)
    .filter((geom): geom is Polygon => !!geom && geom.type === "polygon");

  if (!polygons.length) return null;

  // (Optional) sanity-check SR matches for all; unionOperator requires same SR
  const sr = polygons[0]?.spatialReference;
  const mixedSR = polygons.some((p) => p.spatialReference?.wkid !== sr?.wkid);
  if (mixedSR) {
    // You may want to project to a common SR before unioning (e.g., Web Mercator).
    // For brevity, we bail out here.
    console.warn("combineExtentUsingUnionOperator: mixed spatial references; project first.");
    return null;
  }

  // Perform the topological union on the set of polygons
  // unionOperator.executeMany returns a single geometry or null
  const unionGeometry = unionOperator.executeMany(polygons) as Geometry | null;

  // If union succeeded and has an extent, use it
  const unionExtent: Extent | null =
    unionGeometry && (unionGeometry as any).extent
      ? ((unionGeometry as any).extent as Extent)
      : null;

  // Fallback: combine extents if union failed or no extent present
  let combinedExtent: Extent | null = null;
  for (const p of polygons) {
    const ext = p.extent;
    if (!ext) continue;
    combinedExtent = combinedExtent ? combinedExtent.union(ext) : ext;
  }

  if (!unionGeometry && !combinedExtent) return null;

  return {
    unionGeometry: unionGeometry ?? (combinedExtent as unknown as Geometry),
    extent: unionExtent ?? (combinedExtent as Extent),
  };
}



/**
 * A parcel container with its constituent graphics and a unified bounding extent.
 *
 * @property graphics - The ArcGIS graphics belonging to this parcel group.
 * @property extent - The computed bounding extent, or null if none could be calculated.
 * @extends {Graphic[]}
 */
export interface ParcelGroup {
  graphics:Graphic[]
  extent: Extent|null
}

/**
 * Groups graphics into connected components where each group's features either intersect, touch, or are within a distance threshold of each other.
 *
 * Uses DFS to cluster nearby polygon graphics, also computing a bounding extent per group.
 *
 * @param graphics - The ArcGIS graphics to cluster.
 * @param distanceThresholdMiles - Maximum distance between features in the same group, in miles (defaults to 5).
 * @returns An array of ParcelGroup objects, each containing connected graphics and their computed extent.
 */
export function groupParcels(graphics: Graphic[], distanceThresholdMiles = 5): ParcelGroup[] {
  const visited = new Set<number>();
  const groups: ParcelGroup[] = [];
  const distanceThresholdMeters = distanceThresholdMiles * 1609.34;

  function dfs(index: number, group: ParcelGroup) {
    visited.add(index);
    const currentGraphic = graphics[index];
    if(!currentGraphic) return;
    group.graphics.push(currentGraphic);

    for (let i = 0; i < graphics.length; i++) {
      if (visited.has(i)) continue;
      const otherGraphic = graphics[i];
      if(!otherGraphic) continue;
      const intersects = intersectsOperator.execute(currentGraphic?.geometry as any, otherGraphic?.geometry  as any);
      const touches = touchesOperator.execute(currentGraphic?.geometry  as any, otherGraphic?.geometry  as any);
      const distance = distanceOperator.execute(currentGraphic?.geometry  as any, otherGraphic?.geometry  as any);

      if (intersects || touches || (distance !== null && distance <= distanceThresholdMeters)) {
        dfs(i, group);
      }
    }
  }

  for (let i = 0; i < graphics.length; i++) {
    if (!visited.has(i)) {
      const group: ParcelGroup = {graphics:[], extent:null};
      dfs(i, group);
      groups.push(group);
    }
  }
  groups.forEach((group) => {
    const combinedExtent = combineExtent(group.graphics)?.extent
    group.extent = combinedExtent ? combinedExtent : null;
  })
  return groups;
}

